import {
  Button,
  IconButton,
  Input,
  InputAdornment,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { useStoreState, useStoreActions } from "../../models/RootStore";
import { t } from "ttag";
import ServiceNavigationBar from "../../widgets/Menu/ServiceNavigationBar";
import SimpleModal from "../../widgets/SimpleModal/SimpleModal";
import useStyles from "./VirtualDevices.style";
import { Close, Search } from "@mui/icons-material";
import AddEditMockDevice from "./AddEditMockDevice";
import VirtualDevicesTable from "./VirtualDevicesTable";

const MAX_CONCURRENT_SIMULATOR_CONNECTIONS = 3;
const REGISTRATION_POLL_INTERVAL_MS = 10_000;

const VirtualDevices = (props: any) => {
  const styles: any = useStyles();
  const allMockDevices = useStoreState((state) => state.devices.allMockDevices);
  const getAllMockDevices = useStoreActions((actions) => actions.devices.getAllMockDevices);
  const createMockDevice = useStoreActions((actions) => actions.devices.createMockDevice);
  const updateMockDevice = useStoreActions((actions) => actions.devices.updateMockDevice);
  const deleteMockDevice = useStoreActions((actions) => actions.devices.deleteMockDevice);
  const setMockDeviceRegistration = useStoreActions((actions) => actions.devices.setMockDeviceRegistration);
  const resetAllMockDevices = useStoreActions((actions) => actions.devices.resetAllMockDevices);

  useEffect(() => {
    getAllMockDevices();
    const intervalId = setInterval(getAllMockDevices, REGISTRATION_POLL_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, []);

  const [searchValue, setSearchValue] = useState<string>("");
  const [openAddEdit, setOpenAddEdit] = useState<any>(null);
  const [deviceToDelete, setDeviceToDelete] = useState<any>(null);
  const [deviceToUnregister, setDeviceToUnregister] = useState<any>(null);
  const [showResetConfirm, setShowResetConfirm] = useState<boolean>(false);
  const [expandedDeviceId, setExpandedDeviceId] = useState<string>("");

  const devicesList = useMemo(() => Object.values(allMockDevices || {}), [allMockDevices]);

  const concurrentSimulatorConnections = useMemo(() => devicesList.filter(
    (device: any) => device.simulatorConnected && device.connected
  ).length, [devicesList]);

  const filteredDevices = useMemo(() => devicesList.filter((device: any) => {
    if (!searchValue) {
      return true;
    }

    const search = searchValue.toUpperCase();
    const name = (device.name || "").toUpperCase();
    const serial = (device.serial || "").toUpperCase();
    return name.indexOf(search) >= 0 || serial.indexOf(search) >= 0;
  }), [devicesList, searchValue]);

  const plainVirtualDevices = useMemo(
    () => filteredDevices.filter((device: any) => !device.simulatorConnected),
    [filteredDevices]
  );

  const simulatedDevices = useMemo(
    () => filteredDevices.filter((device: any) => device.simulatorConnected),
    [filteredDevices]
  );

  const handleSearchChange = (event: any) => {
    setSearchValue(event.target.value);
  };

  const handleClearSearch = () => {
    setSearchValue("");
  };

  const handleOpenAdd = () => {
    setOpenAddEdit("new");
  };

  const handleCloseAddEdit = () => {
    setOpenAddEdit(null);
  };

  const handleRowDoubleClick = (id: string) => {
    setOpenAddEdit(id);
  };

  const handleToggleExpand = (id: string) => {
    setExpandedDeviceId(expandedDeviceId === id ? "" : id);
  };

  const handleSave = (values: any) => {
    if (openAddEdit === "new") {
      createMockDevice(values);
    } else {
      updateMockDevice({ id: openAddEdit, name: values.name });
    }
    setOpenAddEdit(null);
  };

  const handleRequestDelete = () => {
    setDeviceToDelete(openAddEdit);
    setOpenAddEdit(null);
  };

  const handleRequestDeleteFromRow = (id: string) => {
    setDeviceToDelete(id);
  };

  const handleCancelDelete = () => {
    setDeviceToDelete(null);
  };

  const handleConfirmDelete = () => {
    deleteMockDevice(deviceToDelete);
    setDeviceToDelete(null);
  };

  const handleRequestUnregister = (device: any) => {
    setDeviceToUnregister(device);
  };

  const handleCancelUnregister = () => {
    setDeviceToUnregister(null);
  };

  const handleConfirmUnregister = () => {
    setMockDeviceRegistration({ id: deviceToUnregister.id, registered: false });
    setDeviceToUnregister(null);
  };

  const handleOpenResetConfirm = () => {
    setShowResetConfirm(true);
  };

  const handleCancelReset = () => {
    setShowResetConfirm(false);
  };

  const handleConfirmReset = () => {
    resetAllMockDevices();
    setShowResetConfirm(false);
  };

  const handleCopyValue = (value: string) => {
    navigator.clipboard.writeText(value);
  };

  const deviceToDeleteRecord: any = deviceToDelete ? allMockDevices[deviceToDelete] : null;

  const renderSegment = (titleText: string, subtitleText: string, devices: any[], ariaLabel: string) => {
    if (!devices.length) {
      return null;
    }

    return (
      <div className={styles.segment}>
        <div className={styles.segmentHeader}>
          <div className={styles.segmentTitle}>{titleText}</div>
          <div className={styles.segmentSubtitle}>{subtitleText}</div>
        </div>
        <VirtualDevicesTable
          devices={devices}
          expandedDeviceId={expandedDeviceId}
          onToggleExpand={handleToggleExpand}
          onRowDoubleClick={handleRowDoubleClick}
          onRequestDelete={handleRequestDeleteFromRow}
          onRequestUnregister={handleRequestUnregister}
          onCopyValue={handleCopyValue}
          concurrentSimulatorConnections={concurrentSimulatorConnections}
          maxConcurrentSimulatorConnections={MAX_CONCURRENT_SIMULATOR_CONNECTIONS}
          ariaLabel={ariaLabel}
        />
      </div>
    );
  };

  return (
    <>
      <ServiceNavigationBar
        title={t`Virtual Devices`}
        searchComponent={
          <Input
            placeholder={t`Search by name or serial...`}
            value={searchValue}
            onChange={handleSearchChange}
            disableUnderline={true}
            classes={{ root: styles.inputRoot }}
            startAdornment={
              <InputAdornment position="start" className={styles.searchAdorment}>
                <Search className={styles.searchIcon} />
              </InputAdornment>
            }
            endAdornment={
              searchValue && (
                <IconButton onClick={handleClearSearch} className={styles.closeIconStyle}>
                  <Close />
                </IconButton>
              )
            }
          />
        }
        {...props}
      >
        <div className={styles.buttonsContainer}>
          <Button className={styles.resetButtonStyle} onClick={handleOpenResetConfirm} variant="outlined">
            {t`RESET ALL VIRTUAL DATA`}
          </Button>
          <Button className={styles.buttonStyle} onClick={handleOpenAdd}>
            {t`ADD NEW DEVICE`}
          </Button>
        </div>

        {renderSegment(
          t`Virtual Devices`,
          t`Virtual devices that can be added, but can't be sent commands or connected to a terminal.`,
          plainVirtualDevices,
          "virtual devices table"
        )}

        {renderSegment(
          t`Simulated Devices`,
          t`Virtual devices connected to the simulator — can be added and sent commands.`,
          simulatedDevices,
          "simulated devices table"
        )}

        {openAddEdit && (
          <AddEditMockDevice
            isEdit={openAddEdit !== "new"}
            device={openAddEdit !== "new" ? allMockDevices[openAddEdit] : null}
            onClose={handleCloseAddEdit}
            onSave={handleSave}
            onRequestDelete={handleRequestDelete}
          />
        )}

        <SimpleModal
          showModal={!!deviceToDelete}
          severity="danger"
          title={t`Delete Virtual Device`}
          contentText={deviceToDeleteRecord ? t`Are you sure you want to delete ${deviceToDeleteRecord.name} (${deviceToDeleteRecord.serial})?` : ""}
          actionName={t`Delete`}
          onAction={handleConfirmDelete}
          closeModal={handleCancelDelete}
        />

        <SimpleModal
          showModal={!!deviceToUnregister}
          severity="warning"
          title={t`Unregister Device`}
          contentText={
            deviceToUnregister
              ? t`Note, you are about to unregister a device that completed a real registration process with the system. This will disconnect the device from its linked site/customer (${deviceToUnregister.registeredCustomerName} – ${deviceToUnregister.registeredSiteName}). Continue?`
              : ""
          }
          actionName={t`Yes`}
          cancelActionName={t`No`}
          onAction={handleConfirmUnregister}
          closeModal={handleCancelUnregister}
        />

        <SimpleModal
          showModal={showResetConfirm}
          title={t`Reset All Virtual Data`}
          contentText={t`This will permanently delete every virtual device and all of its hierarchy (units, sensors, systems, etc). This cannot be undone. Are you sure?`}
          actionName={t`Reset All`}
          onAction={handleConfirmReset}
          closeModal={handleCancelReset}
        />
      </ServiceNavigationBar>
    </>
  );
};

export default VirtualDevices;
